import { FC, Fragment, useCallback, useEffect, useState } from 'react';

import { useSelfServiceAccount } from '@eneco/auth-be-self-service';
import { useApplication } from '@eneco/common/application';
import logger from '@eneco/common/log';
import RichText from '@eneco/components/RichText/RichText';
import {
  EnergyProductPair,
  EnergyProductPairMail,
  getEnecoBeXapiSiteApiV1OrdersByOrderId,
  postEnecoBeXapiSiteApiV1PriceSimulationByPriceSimulationIdEmail,
  PriceSimulationProductCombinationOptionDto,
  SimulatePriceSimulationResponse,
} from '@eneco/dc-be/client';
import usePublicDCBE from '@eneco/dc-be/hooks/usePublicDCBE';
import unwrapData from '@eneco/dc-be/utils/unwrapData';
import { typedYupResolver } from '@eneco/hooks/form';
import { useFormatter } from '@eneco/i18n';
import { useRouter } from '@eneco/next';
import {
  Bucket,
  Button,
  Checkbox,
  Form,
  Heading,
  InputEmail,
  NotificationBox,
  Skeleton,
  Stack,
  Stretch,
  Text,
  TextLink,
} from '@eneco/sparky';
import { EnvelopeIcon } from '@eneco/sparky/icons';
import { styled } from '@eneco/sparky/stitches';
import { NotificationBoxProps } from '@eneco/sparky/types';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { PriceSimulationRenderingExtended } from '@sitecore/types/manual/be/PriceSimulationExtended';
import { ProductProposal } from '@sitecore/types/manual/be/ProductList';
import { Fields } from '@sitecore/types/PriceSimulation';
import { useForm, useFormContext } from 'react-hook-form';
import { useSWRConfig } from 'swr';

import PriceSimulationDetailsDialog from './PriceSimulationDetailsDialog';
import ProductCard from './ProductCard';
import { getSelectedProducts } from './ResultStepHelper';
import DataErrorNotification from '../../../../components/DataErrorNotification';
import { buildNavigationHref } from '../../../../utils/buildNavigationHref';
import { getSitecoreProduct } from '../../../../utils/getSitecoreProduct';
import { languageToCustomerLanguageDto } from '../../../../utils/language';
import { iconReplacement, inlineStrongReplacement } from '../../../../utils/richTextReplacements';
import { useOrderId } from '../../../order-management/hooks/useOrderId';
import { syncOrderBasketOrInitiate } from '../../../order-management/utils/syncOrderBasketOrInitiate';
import { usePriceSimulationSimulate } from '../../hooks/usePriceSimulationSimulate';
import { emailProposalFormSchema, EmailProposalFormSchema } from '../../schemas/emailProposalFormSchema';
import { PriceSimulationFormSchema } from '../../schemas/priceSimulationFormSchema';
import { priceSimulationSteps } from '../../types/PriceSimulationSteps';
import { getTrackableFormValues } from '../../utils/getTrackableFormValues';

const mapToMailPair = (pair: EnergyProductPair, enecoProductsList: ProductProposal[]): EnergyProductPairMail => {
  const isElectricityProduct = pair.electric != null;
  const electricSitecoreProduct = isElectricityProduct
    ? getSitecoreProduct({ enecoProductsList, technicalProductName: pair.electric! })
    : undefined;
  const gasSitecoreProduct =
    pair.gas != null ? getSitecoreProduct({ enecoProductsList, technicalProductName: pair.gas }) : undefined;
  return {
    electricTechnicalName: pair.electric ?? null,
    electricVisibleName: electricSitecoreProduct?.productDisplayName.value ?? null,
    gasTechnicalName: pair.gas ?? null,
    gasVisibleName: gasSitecoreProduct?.productDisplayName.value ?? null,
  };
};

export const PriceSimulationResultStep = () => {
  const { fields } = useContent<PriceSimulationRenderingExtended>();
  const { query, push } = useRouter();
  const { trackEventBE } = useTracking();
  const { language } = useApplication();
  const { selectedAccount } = useSelfServiceAccount();
  const { getValues } = useFormContext<PriceSimulationFormSchema>();
  const [showDetailsIndex, setShowDetailsIndex] = useState<number>(0);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [emailNotification, setEmailNotification] = useState<NotificationBoxProps | null>(null);
  const [errorInitiatingOrder, setErrorInitiatingOrder] = useState<NotificationBoxProps | null>(null);
  const [isInitiatingOrder, setIsInitiatingOrder] = useState(false);
  const [lastEmailSubmitTime, setLastEmailSubmitTime] = useState<number | null>(null);

  const PRICE_SIMULATION_EMAIL_COOLDOWN_TIME = 60_000;

  const { mutate } = useSWRConfig();

  const city = getValues('city') ?? '';

  const emailForm = useForm<EmailProposalFormSchema>({
    mode: 'onTouched',
    resolver: typedYupResolver<EmailProposalFormSchema>(emailProposalFormSchema(fields)),
  });

  const {
    register,
    formState: { errors, isValid },
  } = emailForm;

  const priceSimulationId = query['priceSimulationId'] as string;
  const orderId = useOrderId();

  const { data: orderDataRaw } = usePublicDCBE(
    getEnecoBeXapiSiteApiV1OrdersByOrderId,
    { path: { orderId: orderId ?? '' } },
    [`/orders/${orderId}`],
    !!orderId,
  );
  const orderData = orderId ? unwrapData(orderDataRaw) : undefined;

  const onSubmit = async (data: EmailProposalFormSchema) => {
    const isEmailOnCooldown =
      lastEmailSubmitTime !== null && Date.now() - lastEmailSubmitTime < PRICE_SIMULATION_EMAIL_COOLDOWN_TIME;

    if (isEmailOnCooldown) return;

    try {
      const { fixedProducts, variableProducts, dynamicProducts } = getSelectedProducts(
        selectedEnergyTypes as string,
        fields.productProposal.enecoProductsList,
        fields.productProposal.enecoProductsBundlesList,
      );

      const { response } = await postEnecoBeXapiSiteApiV1PriceSimulationByPriceSimulationIdEmail({
        path: { priceSimulationId },
        body: {
          email: data.email,
          promotionalContentOptIn: data.emailConsent ?? false,
          language: languageToCustomerLanguageDto(language),
          variableProductsData: variableProducts.map(p => mapToMailPair(p, fields.productProposal.enecoProductsList)),
          fixedProductsData: fixedProducts.map(p => mapToMailPair(p, fields.productProposal.enecoProductsList)),
          dynamicProductsData: dynamicProducts.map(p => mapToMailPair(p, fields.productProposal.enecoProductsList)),
        },
      });

      if (!response.ok) {
        trackEventBE('pbt_email', {
          status: 'failed',
          message: 'Failed to send email: ' + response.statusText,
          data: { ...getTrackableFormValues(getValues()), items: orderData?.basket?.items },
        });
        throw new Error('Failed to send email: ' + response.statusText);
      }
      trackEventBE('pbt_email', {
        status: 'success',
        data: { ...getTrackableFormValues(getValues()), items: orderData?.basket?.items },
      });
      setLastEmailSubmitTime(Date.now());
      setEmailNotification({
        isAlert: true,
        variant: 'success',
        title:
          fields.proposalStep.emailProposalSuccessNotification?.value.title ??
          'Email sent successfully [not in sitecore]',
        text:
          fields.proposalStep.emailProposalSuccessNotification?.value.content ??
          'Email sent successfully [not in sitecore]',
      });
    } catch (error) {
      logger.error('Failed to send email', error instanceof Error ? error.message : String(error));
      setEmailNotification({
        isAlert: true,
        title:
          fields.proposalStep.emailProposalErrorNotification?.value.title ?? 'Failed to send email [not in sitecore]',
        text: (
          <RichText
            html={
              fields.proposalStep.emailProposalErrorNotification?.value.content ??
              'Failed to send email [not in sitecore]'
            }
          />
        ),
        variant: 'error',
      });
      trackEventBE('pbt_email', {
        status: 'failed',
        message: 'Failed to send email',
        data: { ...getTrackableFormValues(getValues()), items: orderData?.basket?.items },
      });
    }
  };

  const energyType = getValues('energyType');

  const { fixedProducts, variableProducts, dynamicProducts } = getSelectedProducts(
    energyType as string,
    fields.productProposal.enecoProductsList,
    fields.productProposal.enecoProductsBundlesList,
  );

  const {
    data,
    isValidating: isLoadingPriceSimulationData,
    error: errorPriceSimulationData,
  } = usePriceSimulationSimulate({
    priceSimulationId,
    variableProducts,
    fixedProducts,
    dynamicProducts,
    enecoProductsList: fields.productProposal.enecoProductsList,
  });

  useEffect(() => {
    if (data?.productCombinationOptions)
      trackEventBE('pbt_view_item_list', {
        status: 'pageView',
        data: { ...getTrackableFormValues(getValues()), productCombinationOptions: data?.productCombinationOptions },
      });
  }, [data?.productCombinationOptions, getValues, trackEventBE]);

  const selectedEnergyTypes = energyType || data?.simulationDetails.energyType;

  const onSelectProductClick = useCallback(
    async (productOption: PriceSimulationProductCombinationOptionDto) => {
      try {
        setIsInitiatingOrder(true);
        const resolvedOrderId = await syncOrderBasketOrInitiate({
          orderId,
          orderData,
          accountNumber: selectedAccount?.crmAccountNumber ?? '',
          language,
          priceSimulationId,
          electricityProductTechnicalName: productOption.electricityProduct?.productName,
          gasProductTechnicalName: productOption.gasProduct?.productName,
          promoId: productOption.promoDisclaimers?.[0]?.promoId,
        });
        const targetOrderId = resolvedOrderId ?? orderId;
        if (!targetOrderId) {
          throw new Error('Order ID is missing');
        }
        const isSoho = data?.simulationDetails.customerType === 'SOHO';
        trackEventBE('pbt_add_to_cart', {
          status: 'click',
          data: { ...getTrackableFormValues(getValues()), productOption },
        });
        mutate(`/orders/${targetOrderId}`);
        push(
          buildNavigationHref(fields.navigation.orderManagementLink?.value.href ?? '/my-eneco/order-management', {
            queryParams: {
              orderId: targetOrderId,
              ...(priceSimulationId ? { priceSimulationId } : {}),
              isSoho,
            },
          }),
        );
      } catch (error) {
        logger.error('Failed to initiate order', error instanceof Error ? error.message : String(error));
        setErrorInitiatingOrder({
          isAlert: true,
          title: fields.summaryStep.errorNotification.value.title,
          text: <RichText html={fields.summaryStep.errorNotification.value.content} />,
          variant: 'error',
        });
      } finally {
        setIsInitiatingOrder(false);
      }
    },
    [
      orderId,
      orderData,
      selectedAccount?.crmAccountNumber,
      language,
      priceSimulationId,
      data?.simulationDetails.customerType,
      trackEventBE,
      getValues,
      mutate,
      push,
      fields.navigation.orderManagementLink?.value.href,
      fields.summaryStep.errorNotification.value.title,
      fields.summaryStep.errorNotification.value.content,
    ],
  );

  const amountOfPromosInRibbon = Math.max(
    ...(data?.productCombinationOptions?.map(option => option?.promoDisclaimers?.length || 0) || []),
  );

  const hasAnyPromotionInList =
    !isLoadingPriceSimulationData &&
    (data?.productCombinationOptions?.some(option => option?.promoDisclaimers?.some(x => x.promoId)) ?? false);

  return (
    <>
      <Stack gap="2">
        <Heading as="h1" size="M">
          {fields.proposalStep.title.value}
        </Heading>
        <Text>{fields.proposalStep.subTitle.value}</Text>
      </Stack>
      <Stack gap={6}>
        <Stretch>
          <Stack gap="4" direction={{ initial: 'column', lg: 'row' }}>
            {isLoadingPriceSimulationData ? (
              <>
                {/* 2 dummy products in loading state to show skeletons */}
                <ProductCard index={0} isLoading={true} />
                <ProductCard index={1} isLoading={true} />
              </>
            ) : errorPriceSimulationData ? (
              <DataErrorNotification />
            ) : (
              data?.productCombinationOptions
                .sort((n1, n2) => {
                  if (n1.sortOrder > n2.sortOrder) return 1;
                  if (n1.sortOrder < n2.sortOrder) return -1;
                  return 0;
                })
                .map((option, idx) => (
                  <ProductCard
                    key={`${option.electricityProduct?.productName ?? option.gasProduct?.productName}-${idx}`}
                    index={idx}
                    productOption={option}
                    onClickShowDetails={idx => {
                      setShowDetailsIndex(idx);
                      setShowDetailsModal(true);
                    }}
                    onSelectProductClick={onSelectProductClick}
                    isProductABundle={data?.simulationDetails.energyType === 'Both'}
                    isLoading={isLoadingPriceSimulationData || isInitiatingOrder}
                    amountOfPromosInRibbon={amountOfPromosInRibbon}
                    showRibbonArea={hasAnyPromotionInList}
                  />
                ))
            )}
          </Stack>
        </Stretch>

        <Stack.Item>
          <ContractDetailDisclaimers
            city={city}
            fields={fields}
            data={data ?? null}
            priceSimulationId={priceSimulationId}
            orderId={orderId}
            isLoading={isLoadingPriceSimulationData || isInitiatingOrder}
          />
        </Stack.Item>
        <Stack.Item>
          <Text color="textLowEmphasis" size="BodyXS">
            <RichText
              html={
                fields.summaryStep?.contractDetailsDisclaimerDescription?.value ??
                `* Promo Eneco Zon & Wind Vast + Aardgas Vast, aanbod geldig tot en met 31/05/2025. [not in sitecore]`
              }
            />
          </Text>
        </Stack.Item>

        {errorInitiatingOrder && <NotificationBox {...errorInitiatingOrder} />}
        {isLoadingPriceSimulationData ? (
          <Bucket title={<Skeleton width={'300px'} height={36} />}>
            <Bucket.Content>
              <Skeleton width={'500px'} height={36} />
              <Stack direction="column" gap="6">
                <Stack direction="row" gap="4">
                  <Skeleton width={'30px'} height={'50px'} />
                  <Skeleton width={'300px'} height={'50px'} />
                </Stack>
                <Stack gap="2">
                  <Text size="BodyS" weight="bold">
                    <Skeleton width={'100px'} height={'50px'} />
                  </Text>
                  <Stack direction={{ initial: 'column', md: 'row' }} gap="2">
                    <Skeleton width={'225px'} height={'100px'} />
                    <Stack.Item>
                      <Stretch height={false}>
                        <Skeleton width={'150px'} height={'100px'} />
                      </Stretch>
                    </Stack.Item>
                  </Stack>
                </Stack>
              </Stack>
            </Bucket.Content>
          </Bucket>
        ) : (
          <Bucket title={<span>{fields.proposalStep.emailProposalTitle.value}</span>}>
            <Bucket.Content>
              <RichText html={fields.proposalStep.emailProposalSubTitle.value} />
              <Form onSubmit={emailForm.handleSubmit(onSubmit)}>
                <Stack direction="column" gap="6">
                  <Checkbox
                    label={fields.proposalStep.emailConsentFormField?.value.label ?? 'Email consent [not in Sitecore]'}
                    error={errors.emailConsent?.message}
                    {...register('emailConsent')}
                  />
                  <Stack gap="2">
                    <Text size="BodyS" weight="bold">
                      <label htmlFor={fields.proposalStep.emailFormField.value.label}>
                        {fields.proposalStep.emailFormField.value.label}
                      </label>
                    </Text>
                    <Stack direction={{ initial: 'column', md: 'row' }} gap="2">
                      <StyledInputEmail
                        label={''}
                        aria-label={fields.proposalStep.emailFormField.value.label}
                        autoComplete="email"
                        error={errors.email?.message}
                        {...register('email')}
                      />
                      <Stack.Item>
                        <Stretch height={false}>
                          <Button type="submit" size="compact" isDisabled={!isValid}>
                            <EnvelopeIcon />
                            {fields.proposalStep.submitEmailButtonText.value}
                          </Button>
                        </Stretch>
                      </Stack.Item>
                    </Stack>
                  </Stack>
                </Stack>
              </Form>
              {emailNotification && <NotificationBox {...emailNotification} />}
            </Bucket.Content>
          </Bucket>
        )}
        <Text color="textLowEmphasis" size="BodyXS">
          {data?.productCombinationOptions.map((productCombinationOption, index) => (
            <Fragment key={index}>
              {productCombinationOption.promoDisclaimers.map(promoDisclaimer => (
                <StyledParagraph key={promoDisclaimer.promoId}>{promoDisclaimer.disclaimerText}</StyledParagraph>
              ))}
            </Fragment>
          ))}
          <RichText html={fields.proposalStep.description.value} />
        </Text>
      </Stack>
      <PriceSimulationDetailsDialog
        fields={fields}
        data={data}
        showDetailsIndex={showDetailsIndex}
        isOpen={showDetailsModal}
        setOpen={setShowDetailsModal}
      />
    </>
  );
};

const StyledInputEmail = styled(InputEmail, {
  width: '100%',
  '@md': {
    width: '400px',
    marginTop: '-$2',
  },
});

const StyledParagraph = styled('p', {
  marginBottom: '$4',
  '&:last-child': {
    marginBottom: 0,
  },
});

type ContractDetailDisclaimersProps = {
  fields: Fields;
  city: string;
  data: SimulatePriceSimulationResponse | null;
  priceSimulationId: string;
  orderId: string | undefined;
  isLoading: boolean;
};
const ContractDetailDisclaimers: FC<ContractDetailDisclaimersProps> = ({
  fields,
  city,
  data,
  priceSimulationId,
  orderId,
  isLoading,
}) => {
  const { format, unit } = useFormatter();

  if (isLoading) {
    return <Skeleton width={80} height={36} />;
  }

  return (
    <>
      <RichText
        html={format(fields.summaryStep.contractDetailsOfferDescription.value, {
          city,
        })}
        replacements={{ ...inlineStrongReplacement, ...iconReplacement }}
      />
      {(data?.simulationDetails.usage?.electricity?.multipleMeterUsage?.peakUsage != null ||
        data?.simulationDetails.usage?.electricity?.multipleMeterUsage?.offPeakUsage != null) &&
        data?.simulationDetails.features.meterSubType === 'Multiple' && (
          <RichText
            html={format(fields.summaryStep.contractDetailsElecDayNightDescription.value, {
              valueDay: unit.electricity(data?.simulationDetails.usage?.electricity?.multipleMeterUsage?.peakUsage),
              valueNight: unit.electricity(
                data?.simulationDetails.usage?.electricity?.multipleMeterUsage?.offPeakUsage,
              ),
            })}
            replacements={{ ...inlineStrongReplacement, ...iconReplacement }}
          />
        )}
      {data?.simulationDetails.usage?.electricity?.singularMeterUsage?.totalUsage != null &&
        data?.simulationDetails.features.meterSubType === 'Single' && (
          <RichText
            html={format(fields.summaryStep.contractDetailsElecSingleDescription.value, {
              value: unit.electricity(data?.simulationDetails.usage?.electricity?.singularMeterUsage.totalUsage),
            })}
            replacements={{ ...inlineStrongReplacement, ...iconReplacement }}
          />
        )}
      {data?.simulationDetails.usage?.electricity?.singularMeterUsage?.exclusiveNightUsage != null &&
        data?.simulationDetails.features.hasExclusiveNightMeter && (
          <RichText
            html={format(fields.summaryStep.contractDetailsElecExclNightDescription.value, {
              value: unit.electricity(
                data?.simulationDetails.usage?.electricity?.singularMeterUsage.exclusiveNightUsage,
              ),
            })}
          />
        )}
      {data?.simulationDetails.usage?.gas?.totalUsage && (
        <RichText
          html={format(fields.summaryStep.contractDetailsGasDescription.value, {
            value: unit.electricity(data?.simulationDetails.usage?.gas.totalUsage),
          })}
          replacements={{ ...inlineStrongReplacement, ...iconReplacement }}
        />
      )}

      {data?.simulationDetails.usage?.electricity?.solarPanelsProduction?.peakHoursInjection !== null &&
        data?.simulationDetails.usage?.electricity?.solarPanelsProduction?.offPeakHoursInjection !== null && (
          <RichText
            html={format(fields.summaryStep?.contractDetailsInjectionDayNightDescription?.value, {
              valueDay: unit.produced(
                data?.simulationDetails.usage?.electricity?.solarPanelsProduction?.peakHoursInjection ?? 0,
              ),
              valueNight: unit.produced(
                data?.simulationDetails.usage?.electricity?.solarPanelsProduction?.offPeakHoursInjection ?? 0,
              ),
            })}
            replacements={{ ...inlineStrongReplacement, ...iconReplacement }}
          />
        )}

      {data?.simulationDetails.usage?.electricity?.solarPanelsProduction?.totalInjection !== null && (
        <RichText
          html={format(fields.summaryStep?.contractDetailsInjectionSingleDescription?.value, {
            value: unit.produced(
              data?.simulationDetails.usage?.electricity?.solarPanelsProduction?.totalInjection ?? 0,
            ),
          })}
          replacements={{ ...inlineStrongReplacement, ...iconReplacement }}
        />
      )}

      {data?.simulationDetails.usage?.electricity?.solarPanelsProduction?.installationTotalKva !== null && (
        <RichText
          html={format(fields.summaryStep?.contractDetailsInjectionSingleDescription?.value, {
            value: unit.kva(
              data?.simulationDetails.usage?.electricity?.solarPanelsProduction?.installationTotalKva ?? 0,
            ),
          })}
          replacements={{ ...inlineStrongReplacement, ...iconReplacement }}
        />
      )}

      <EditPriceSimulationLink
        fields={fields}
        priceSimulationId={priceSimulationId}
        orderId={orderId}
        productCombinationOptions={data?.productCombinationOptions}
      />
    </>
  );
};

const EditPriceSimulationLink = ({
  fields,
  priceSimulationId,
  orderId,
  productCombinationOptions,
}: {
  priceSimulationId: string;
  orderId: string | undefined;
  fields: Fields;
  productCombinationOptions: PriceSimulationProductCombinationOptionDto[] | undefined;
}) => {
  const { activePath } = useRouter();
  const { trackEventBE } = useTracking();
  const { getValues } = useFormContext<PriceSimulationFormSchema>();

  return (
    <>
      {`. `}
      <TextLink
        emphasis="low"
        onClick={() => {
          trackEventBE('pbt_adapt', {
            status: 'click',
            data: { ...getTrackableFormValues(getValues()), productCombinationOptions },
          });
          window.location.href = buildNavigationHref(activePath, {
            queryParams: {
              priceSimulationId,
              orderId,
            },
            stepHash: priceSimulationSteps.energyTypeCheck,
          });
        }}>
        {fields.summaryStep.contractDetailsEditDataDescription?.value ?? 'Mijn verbruik aanpassen [not in sitecore]'}
      </TextLink>
    </>
  );
};
